import { Link } from "react-router-dom";
import { CATEGORY_META, tx } from "@/lib/i18n";
import { useLang } from "@/contexts/LangContext";
import { ArrowUpRight, Calendar, Building2, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

function StatusBadge({ post, lang }) {
  const now = new Date();
  const cat = post.category;
  let label = null;
  let className = "";
  if (cat === "results") {
    label = lang === "hi" ? "परिणाम जारी" : "Result Out";
    className = "bg-emerald-50 text-emerald-700 border-emerald-200";
  } else if (cat === "admit-card") {
    label = lang === "hi" ? "उपलब्ध" : "Available";
    className = "bg-sky-50 text-sky-700 border-sky-200";
  } else if (cat === "answer-key") {
    label = lang === "hi" ? "जारी" : "Released";
    className = "bg-sky-50 text-sky-700 border-sky-200";
  } else if (post.application_last_date) {
    const d = new Date(post.application_last_date);
    if (!isNaN(d)) {
      const days = Math.round((d - now) / (86400000));
      if (days >= 0 && days <= 10) {
        label = lang === "hi" ? `${days}द बाकी` : `${days}d left`;
        className = "bg-orange-50 text-orange-700 border-orange-200";
      } else if (days > 10) {
        label = lang === "hi" ? "आवेदन खुला" : "Open";
        className = "bg-emerald-50 text-emerald-700 border-emerald-200";
      }
    }
  }
  if (post.is_featured && !label) {
    label = lang === "hi" ? "विशेष" : "Featured";
    className = "bg-slate-900 text-white border-slate-900";
  }
  if (!label) {
    label = lang === "hi" ? "नया" : "New";
    className = "bg-slate-100 text-slate-700 border-slate-200";
  }
  return (
    <span className={`inline-flex items-center text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-sm border ${className}`}>
      {label}
    </span>
  );
}

export function PostCard({ post, dense = false }) {
  const { lang } = useLang();
  const title = (lang === "hi" && post.title_hi) ? post.title_hi : post.title_en;
  return (
    <Link
      to={`/post/${post.slug}`}
      data-testid={`post-card-${post.slug}`}
      className={`card-hover group block bg-white border border-slate-200 rounded-md p-4 ${dense ? "" : "sm:p-5"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <StatusBadge post={post} lang={lang} />
        <ArrowUpRight
          size={16}
          className="text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0"
        />
      </div>
      <h3 className="font-display font-semibold text-[15px] leading-snug text-slate-900 line-clamp-3 mb-3">
        {title}
      </h3>
      <div className="space-y-1.5 text-[12px] text-slate-600">
        {post.organization && (
          <div className="flex items-start gap-1.5">
            <Building2 size={13} className="mt-0.5 flex-shrink-0 text-slate-400" />
            <span className="line-clamp-1">{post.organization}</span>
          </div>
        )}
        {post.application_last_date && (
          <div className="flex items-center gap-1.5">
            <Calendar size={13} className="flex-shrink-0 text-slate-400" />
            <span className="font-mono-tabular">
              {tx("ui.last_date", lang)}: <strong className="text-slate-900">{post.application_last_date}</strong>
            </span>
          </div>
        )}
        {post.state && (
          <div className="flex items-center gap-1.5">
            <MapPin size={13} className="flex-shrink-0 text-slate-400" />
            <span>{post.state}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

export function SectionHeader({ title, subtitle, viewAllHref, testId }) {
  const { lang } = useLang();
  return (
    <div className="flex items-end justify-between mb-5 pb-3 border-b border-slate-200">
      <div>
        <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-orange-600 mb-1">
          {subtitle}
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {title}
        </h2>
      </div>
      {viewAllHref && (
        <Link
          to={viewAllHref}
          data-testid={testId || "view-all-link"}
          className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 transition-colors"
        >
          {tx("ui.view_all", lang)} <ArrowUpRight size={14} />
        </Link>
      )}
    </div>
  );
}
